import { Linking, Platform, RefreshControl, ScrollView, StyleSheet, SafeAreaView } from 'react-native';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFocusEffect, useLinkTo, useNavigation, useIsFocused } from '@react-navigation/native';
import {
  useGetDataSharing,
  useGetDataSharingModal,
  useGetDataSharingRequestList,
  useSetDataSharing
} from 'src/core/hooks/useDataSharing';

import DynamicCard from 'src/features/home/DynamicCard';
import { DynamicCardBody } from 'src/core/types/DynamicCard';
import { ErrorModal } from 'src/components/shared/ErrorModal';
import InsightCardsList from 'src/features/home/InsightCardsList';
import { InvitationTenant } from 'src/components/tenants/modals/InvitationTenant';
import { Layout } from '@ui-kitten/components';
import { OutdatedTenant } from 'src/components/tenants/modals/OutdatedTenant';
import ScoreDialContainer from 'src/features/home/ScoreDialContainer';
import { getWebPlatformUrl } from 'src/core/helpers/URLHelper';
import { useDynamicCardsEffect } from 'src/core/hooks/useDynamicCards';
import { useScoreCategoriesEffect } from 'src/core/hooks/useScoreCategories';
import { useAppStore } from 'src/core/store';
import { BackendApi } from 'src/api/shared';
import WebUserService from 'src/api/user';
import { InfoModal } from 'src/components/shared/InfoModal';
import { useGetUserAttributes } from 'src/core/hooks/useUser';
import moment from 'moment';
import { getValue, setValue } from 'src/api/storage';

export interface PillarDynamicCard {
  displayName: string;
  externalKey: string;
  score: number;
  accuracy: number;
  default?: boolean;
}

const styleContainer = StyleSheet.create({
  layout: { flex: 1 },
  safeAreaView: { flex: 1 }
});

// This ref tracks if the welcome campaign modal has been *successfully shown*
// during the current app session. It resets only on app close/reopen.
const hasWelcomeCampaignBeenShownThisSession = { current: false };

const MIN_DAYS_ASK_OPEN_HEALTH_APP = 1; // 1 day
const ASK_OPEN_HEALTH_APP_FREQUENCY = 1; // 1 day

const HomeScreen = (): React.JSX.Element => {
  const isScreenFocused = useIsFocused();
  const navigation = useNavigation();
  const linkTo = useLinkTo();
  const [selectedPillar, setSelectedPillar] = useState<PillarDynamicCard>({
    displayName: '',
    externalKey: '',
    score: 0,
    accuracy: 0
  });
  const [visibleCampaignModal, setVisibleCampaignModal] = useState<boolean>(false);
  const [werableDataIsOutdated, setsetWerableDataIsOutdated] = useState<boolean>(false);
  const [visibleReconnectModal, setVisibleReconnectModal] = useState<boolean>(false);
  const userAttributes = useGetUserAttributes();
  const lastSyncDate = useAppStore((state) => state.lastSyncDate);

  const isCompletedProfile = useMemo(() => {
    return !Object.entries(userAttributes).some(
      ([key, value]) =>
        key !== 'picture' && key !== 'publicEmail' && key !== 'nickname' && (value === undefined || value === '')
    );
  }, [userAttributes]);

  const campaignTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true); // Ref to track component mount status

  const checkWearableDataIsOutdated = useCallback(async () => {
    const lastTimeOpenPopup = await getValue('LastTimeOpenPopup');
    if (lastSyncDate != undefined) {
      const today = moment(new Date());
      const daysDiff = today.diff(lastSyncDate, 'days');
      if (
        daysDiff >= MIN_DAYS_ASK_OPEN_HEALTH_APP &&
        (lastTimeOpenPopup == '' || today.diff(moment(lastTimeOpenPopup), 'days') >= ASK_OPEN_HEALTH_APP_FREQUENCY)
      ) {
        setsetWerableDataIsOutdated(true);
      }
    }
  }, [lastSyncDate]);

  const checkWelcomeCampaign = useCallback(async () => {
    // Clear any existing timeout immediately if this function is re-triggered
    // This prevents multiple timeouts from being active.
    if (campaignTimeoutRef.current) {
      clearTimeout(campaignTimeoutRef.current);
      campaignTimeoutRef.current = null;
    }

    // Only proceed if the screen is currently focused and component is mounted
    if (!isScreenFocused || !isMountedRef.current) {
      return;
    }

    // --- NEW LOGIC: Check global session flag. If already shown, do not re-open. ---
    if (hasWelcomeCampaignBeenShownThisSession.current) {
      return; // Exit if already shown this session
    }
    // --- END NEW LOGIC ---

    try {
      if (isCompletedProfile) {
        const mergeStatus = await WebUserService.getUserMergeStatus();
        const response = await BackendApi.get('surveys/has-completed-welcome-campaign');

        if (mergeStatus === false && response?.data === false) {
          campaignTimeoutRef.current = setTimeout(() => {
            // This code runs AFTER 3 seconds
            // Check mount and focus status one more time before showing
            if (isMountedRef.current && isScreenFocused) {
              setVisibleCampaignModal(true);
              // --- CRITICAL CHANGE: Set the global flag ONLY when the modal is actually made visible ---
              hasWelcomeCampaignBeenShownThisSession.current = true;
            }
          }, 3000);
        }
      }
    } catch (error) {
      console.error('Error checking welcome campaign:', error);
      setVisibleCampaignModal(false); // Hide if there's an error in fetching conditions
    }
  }, [isCompletedProfile, isScreenFocused]); // Dependencies remain the same

  const handleUpdateWearableStatus = useCallback(
    async (shouldRedirect: boolean) => {
      try {
        await BackendApi.patch('terra/update-prompt-wearable-status');
        if (shouldRedirect) {
          navigation.navigate('ConnectedDevices' as any);
        }
      } catch (error) {
        console.error('Error updating wearable status:', error);
      }
    },
    [navigation]
  );

  const checkWearableStatus = useCallback(async () => {
    if (!isScreenFocused || !isMountedRef.current) {
      return;
    }

    try {
      const response = await BackendApi.get('terra/get-prompt-wearable-status');
      if (response?.data && response.data.length > 0) {
        setVisibleReconnectModal(true);
      }
    } catch (error) {
      console.error('Error checking wearable status:', error);
    }
  }, [isScreenFocused]);

  useEffect(() => {
    isMountedRef.current = true; // Set to true on mount

    // This effect runs when the screen mounts or its focus status changes.
    // We want to trigger `checkWelcomeCampaign` if the screen becomes focused,
    // as it contains the logic to decide if the modal should show for *this session*.
    if (isScreenFocused) {
      checkWelcomeCampaign();
      checkWearableDataIsOutdated();
      checkWearableStatus();
    } else {
      // When the screen loses focus, clear any pending timeout
      // This ensures the modal doesn't pop up if the user navigates away.
      if (campaignTimeoutRef.current) {
        clearTimeout(campaignTimeoutRef.current);
        campaignTimeoutRef.current = null;
      }
    }

    // Cleanup function runs on unmount OR when dependencies change (and before re-running the effect)
    return () => {
      isMountedRef.current = false; // Set to false on unmount
      // Clear timeout on unmount to prevent state updates on unmounted component
      if (campaignTimeoutRef.current) {
        clearTimeout(campaignTimeoutRef.current);
        campaignTimeoutRef.current = null;
      }
    };
  }, [isScreenFocused, checkWelcomeCampaign, checkWearableDataIsOutdated, checkWearableStatus]); // Effect now strictly tied to screen focus and campaign logic

  const [dynamicCardRefreshing, setDynamicCardRefreshing] = useState<boolean>(false);
  const [scoreRefreshing, setScoreRefreshing] = useState<boolean>(false);
  const [visibleInvitation, setVisibleInvitation] = useState<boolean>(false);
  const userDataSharingRequestList = useGetDataSharingRequestList();

  const setDataSharingModal = useAppStore((state) => state.setDataSharingModal);

  const setDataSharing = useSetDataSharing();
  const dataSharing = useGetDataSharing();
  const dataSharingModal = useGetDataSharingModal();

  const [dynamicCards, isDynamicCardLoading, dynamicCardError] = useDynamicCardsEffect({
    refreshing: dynamicCardRefreshing,
    setRefreshing: setDynamicCardRefreshing
  });
  const [scoreCategories, isScoreCategoryLoading, scoreCategoryError] = useScoreCategoriesEffect({
    refreshing: scoreRefreshing,
    setRefreshing: setScoreRefreshing
  });

  const clickCard = (card: DynamicCardBody) => {
    if (card.url?.length > 0) {
      if (Platform.OS === 'web') {
        linkTo(getWebPlatformUrl(card.url));
      } else {
        Linking.openURL(card.url);
      }
    } else {
      const data = scoreCategories.find((item) => card.category.toLocaleLowerCase().includes(item.externalKey));
      if (data) {
        navigation.navigate('Pillar' as any, {
          ...data,
          name: data?.displayName,
          type: data?.externalKey,
          accuracy: data?.nominalTotalAccuracy,
          alertText: card.text
        });
      }
    }
  };

  const refreshScoreDial = useCallback(() => {
    setScoreRefreshing(true);
  }, []);

  useFocusEffect(refreshScoreDial);

  useEffect(() => {
    setVisibleInvitation(dataSharing);
  }, [dataSharing, dataSharingModal]);

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    if (userDataSharingRequestList.length > 0) {
      BackendApi.post(
        '/tenants/check-user-sharing-request-status',
        {
          proUserId: userDataSharingRequestList[0].proUserId,
          tenantKey: userDataSharingRequestList[0].tenantKey
        },
        { signal }
      )
        .then((response) => {
          if (isMountedRef.current && response.data.status === true) {
            setDataSharing(true);
            setDataSharingModal({
              tenantName: userDataSharingRequestList[0].tenantName,
              tenantKey: userDataSharingRequestList[0].tenantKey,
              proUserId: userDataSharingRequestList[0].proUserId,
              proUserName: String(userDataSharingRequestList[0].proUserName),
              status: response.data.status
            });
          }
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.warn('Data sharing request aborted');
          } else {
            console.warn('Error checking data sharing status:', error);
          }
        });
    }

    return () => {
      controller.abort();
    };
  }, [userDataSharingRequestList, setDataSharing, setDataSharingModal]);

  return (
    <Layout level="1" style={styleContainer.layout}>
      <SafeAreaView style={{ flex: 1 }}>
        <ScrollView
          style={styleContainer.safeAreaView}
          alwaysBounceVertical={true}
          refreshControl={
            <RefreshControl
              refreshing={scoreRefreshing && dynamicCardRefreshing}
              onRefresh={() => {
                setDynamicCardRefreshing(true);
                setScoreRefreshing(true);
              }}
            />
          }>
          {(dynamicCards.length > 0 || isDynamicCardLoading) && (
            <DynamicCard card={{ ...dynamicCards[0], isLoading: isDynamicCardLoading }} onClick={clickCard} />
          )}
          <ScoreDialContainer
            scoreCategories={scoreCategories}
            isLoading={isScoreCategoryLoading}
            selectedPillar={selectedPillar}
            setSelectedPillar={setSelectedPillar}
          />
          {(dynamicCards.length > 1 || isDynamicCardLoading) && (
            <InsightCardsList
              insightDynamicCards={dynamicCards.slice(1)}
              isLoading={isDynamicCardLoading}
              onClickItem={clickCard}
            />
          )}
        </ScrollView>
        <ErrorModal
          visible={dynamicCardError !== undefined || scoreCategoryError !== undefined}
          message={{ ...dynamicCardError, ...scoreCategoryError }}
        />
        <InfoModal
          visible={visibleCampaignModal}
          message={
            'Please complete the onboarding to initialise your scores. This is required for the app to work well.'
          }
          closeBtnClick={() => {
            setVisibleCampaignModal(false);
            setTimeout(() => {
              navigation.navigate('KaliFirstSignin' as any, { shouldRedirect: true });
            }, 150);
          }}
        />
        <InfoModal
          visible={!visibleCampaignModal && werableDataIsOutdated}
          message={'Your wearable data is outdated. Please open the health app to sync data.'}
          closeBtnClick={async () => {
            setsetWerableDataIsOutdated(false);
            await setValue('LastTimeOpenPopup', Date().toString());
          }}
        />
        <InfoModal
          message={'You need to reconnect with your wearable to continue syncing your data.'}
          visible={visibleReconnectModal}
          buttonTitle={'OK'}
          secondaryButtonTitle={'Not now'}
          closeBtnClick={() => {
            setVisibleReconnectModal(false);
            handleUpdateWearableStatus(true);
          }}
          secondaryButtonClick={() => {
            setVisibleReconnectModal(false);
            handleUpdateWearableStatus(false);
          }}
        />

        {dataSharingModal.status ? (
          <InvitationTenant
            visible={visibleInvitation}
            onFinalButtonClick={() => {
              setDataSharing(false);
              setVisibleInvitation(false);
            }}
            proUserName={dataSharingModal.proUserName}
            tenantName={dataSharingModal.tenantName}
          />
        ) : (
          <OutdatedTenant
            visible={visibleInvitation}
            onFinalButtonClick={() => {
              setDataSharing(false);
              setVisibleInvitation(false);
            }}
            onSecondaryButton={() => {
              setDataSharing(false);
              setVisibleInvitation(false);
              setTimeout(() => {
                navigation.navigate('DataSharing' as any);
              }, 150);
            }}
            proUserName={dataSharingModal.proUserName}
            tenantName={dataSharingModal.tenantName}
          />
        )}
      </SafeAreaView>
    </Layout>
  );
};

export default HomeScreen;
